import React, { useState, useEffect, useRef } from 'react';
import {
  History,
  Search,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Calendar,
  User,
  Trophy,
  Coins,
  ArrowUpCircle,
  ArrowDownCircle,
  Clock,
  FileText,
  Trash2,
  AlertTriangle,
  X,
  DollarSign,
  Send,
  ArrowRightCircle,
  BadgePlus,
  Award,
  Flame,
  Crown,
  Star
} from 'lucide-react';
import axios from 'axios';

const TransactionsTab = ({ user, loading, setLoading, language, t, showSuccessToast, showErrorToast }) => {
  const [transactions, setTransactions] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [systemStats, setSystemStats] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  // Filters
  const [filters, setFilters] = useState({
    type: 'all',
    search: '',
    startDate: '',
    endDate: ''
  });

  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteMode, setDeleteMode] = useState('single'); // 'single', 'before', 'all'
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBeforeDate, setDeleteBeforeDate] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const initialLoadDone = useRef(false);

  // Load transactions
  const loadTransactions = async (page = 1, currentFilters = filters) => {
    setLocalLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit: 50,
        type: currentFilters.type,
        search: currentFilters.search
      });

      if (currentFilters.startDate) params.append('startDate', currentFilters.startDate);
      if (currentFilters.endDate) params.append('endDate', currentFilters.endDate);

      const response = await axios.get(`/api/admin/transactions?${params}`);
      setTransactions(response.data.transactions || []);
      setPagination(response.data.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 });
    } catch (error) {
      console.error('Error loading transactions:', error);
      setTransactions([]);
    } finally {
      setLocalLoading(false);
    }
  };

  // Load transaction types for filter
  const loadTransactionTypes = async () => {
    try {
      const response = await axios.get('/api/admin/transactions/types');
      setTransactionTypes(response.data.types || []);
    } catch (error) {
      console.error('Error loading transaction types:', error);
      setTransactionTypes([{ value: 'all', label: 'All Transactions' }]);
    }
  };

  // Load summary statistics
  const loadSummary = async () => {
    try {
      const response = await axios.get('/api/admin/transactions/summary?timeframe=24h');
      setSummary(response.data);
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  // Load system commission stats
  const loadSystemStats = async () => {
    try {
      const response = await axios.get('/api/admin/transactions/system-stats');
      setSystemStats(response.data);
    } catch (error) {
      console.error('Error loading system stats:', error);
    }
  };

  // Initial load - only once
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      loadTransactions(1);
      loadTransactionTypes();
      loadSummary();
      loadSystemStats();
    }
  }, []);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Apply filters
  const applyFilters = () => {
    loadTransactions(1, filters);
  };

  // Reset filters
  const resetFilters = () => {
    const defaultFilters = {
      type: 'all',
      search: '',
      startDate: '',
      endDate: ''
    };
    setFilters(defaultFilters);
    loadTransactions(1, defaultFilters);
  };

  // Delete single transaction
  const handleDeleteSingle = async () => {
    if (!deleteTarget) return;

    try {
      await axios.delete(`/api/admin/transactions/${deleteTarget._id}`);
      showSuccessToast(t('transactionDeleted', 'Transaction deleted successfully'));
      setShowDeleteModal(false);
      setDeleteTarget(null);
      loadTransactions(pagination.page, filters);
      loadSummary();
      loadSystemStats();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      showErrorToast(error.response?.data?.message || t('deleteError', 'Failed to delete transaction'));
    }
  };

  // Delete transactions before date
  const handleDeleteBefore = async () => {
    if (!deleteBeforeDate) {
      showErrorToast(t('selectDate', 'Please select a date'));
      return;
    }

    try {
      const response = await axios.delete('/api/admin/transactions/bulk', {
        data: { beforeDate: deleteBeforeDate }
      });
      showSuccessToast(response.data.message);
      setShowDeleteModal(false);
      setDeleteBeforeDate('');
      loadTransactions(1, filters);
      loadSummary();
      loadSystemStats();
    } catch (error) {
      console.error('Error bulk deleting transactions:', error);
      showErrorToast(error.response?.data?.message || t('deleteError', 'Failed to delete transactions'));
    }
  };

  // Delete all transactions
  const handleDeleteAll = async () => {
    if (deleteConfirmText !== 'DELETE ALL') {
      showErrorToast(t('typeConfirmation', 'Please type DELETE ALL to confirm'));
      return;
    }

    try {
      const response = await axios.delete('/api/admin/transactions/bulk', {
        data: { deleteAll: true }
      });
      showSuccessToast(response.data.message);
      setShowDeleteModal(false);
      setDeleteConfirmText('');
      loadTransactions(1, filters);
      loadSummary();
      loadSystemStats();
    } catch (error) {
      console.error('Error deleting all transactions:', error);
      showErrorToast(error.response?.data?.message || t('deleteError', 'Failed to delete transactions'));
    }
  };

  // Open delete modal
  const openDeleteModal = (mode, target = null) => {
    setDeleteMode(mode);
    setDeleteTarget(target);
    setDeleteBeforeDate('');
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  // Get icon for transaction type
  const getTransactionIcon = (type, metadata) => {
    // Tier-specific icons for subscriptions
    if (type === 'subscription_instant' || type === 'subscription_drip') {
      const tier = metadata?.tier;
      const tierIcons = {
        'basic': <Award className="w-4 h-4 text-green-400" />,
        'standard': <Star className="w-4 h-4 text-blue-400" />,
        'plus': <BadgePlus className="w-4 h-4 text-purple-400" />,
        'premium': <Flame className="w-4 h-4 text-orange-400" />,
        'max': <Crown className="w-4 h-4 text-yellow-400" />
      };
      return tierIcons[tier] || <Coins className="w-4 h-4 text-green-400" />;
    }

    const icons = {
      'tournament_created': <Trophy className="w-4 h-4 text-blue-400" />,
      'tournament_joined': <User className="w-4 h-4 text-green-400" />,
      'tournament_left': <User className="w-4 h-4 text-orange-400" />,
      'tournament_cancelled_refund': <RefreshCw className="w-4 h-4 text-yellow-400" />,
      'tournament_prize': <Trophy className="w-4 h-4 text-yellow-400" />,
      'tournament_commission_approver': <Coins className="w-4 h-4 text-purple-400" />,
      'tournament_commission_supervisor': <Coins className="w-4 h-4 text-purple-400" />,
      'tournament_commission_system': <DollarSign className="w-4 h-4 text-emerald-400" />,
      'admin_points_add': <ArrowUpCircle className="w-4 h-4 text-green-400" />,
      'admin_points_remove': <ArrowDownCircle className="w-4 h-4 text-red-400" />,
      'admin_points_set': <Coins className="w-4 h-4 text-blue-400" />,
      'paypal_purchase': <Coins className="w-4 h-4 text-green-400" />,
      'withdrawal_request': <ArrowDownCircle className="w-4 h-4 text-orange-400" />,
      'withdrawal_processed': <ArrowDownCircle className="w-4 h-4 text-green-400" />,
      'giveaway_tournament_created': <Trophy className="w-4 h-4 text-yellow-400" />,
      'giveaway_tournament_cancelled': <RefreshCw className="w-4 h-4 text-orange-400" />,
      'auto_cleanup_refund': <RefreshCw className="w-4 h-4 text-gray-400" />,
      'points_transfer_sent': <Send className="w-4 h-4 text-orange-400" />,
      'points_transfer_received': <ArrowRightCircle className="w-4 h-4 text-green-400" />,
      'bracket_tournament_create': <Trophy className="w-4 h-4 text-purple-400" />,
      'bracket_tournament_prize': <Trophy className="w-4 h-4 text-yellow-400" />,
      'bracket_tournament_refund': <RefreshCw className="w-4 h-4 text-orange-400" />
    };
    return icons[type] || <History className="w-4 h-4 text-gray-400" />;
  };

  // Get color class for points change
  const getPointsColorClass = (change) => {
    if (change > 0) return 'text-green-400';
    if (change < 0) return 'text-red-400';
    return 'text-gray-400';
  };

  // Format points display
  const formatPoints = (change) => {
    if (change > 0) return `+${change.toLocaleString()}`;
    return change.toLocaleString();
  };

  return (
    <div className="transactions-tab">
      {/* Summary Cards */}
      {summary && (
        <div className="transactions-summary">
          <div className="summary-card summary-card-total">
            <div className="summary-icon">
              <History className="w-6 h-6" />
            </div>
            <div className="summary-content">
              <span className="summary-value">{summary.totalStats?.totalTransactions?.toLocaleString() || 0}</span>
              <span className="summary-label">{t('totalTransactions', 'Total Transactions')}</span>
            </div>
          </div>

          <div className="summary-card summary-card-in">
            <div className="summary-icon">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div className="summary-content">
              <span className="summary-value text-green-400">+{summary.totalStats?.totalPointsIn?.toLocaleString() || 0}</span>
              <span className="summary-label">{t('pointsIn', 'Points In')}</span>
            </div>
          </div>

          <div className="summary-card summary-card-out">
            <div className="summary-icon">
              <TrendingDown className="w-6 h-6" />
            </div>
            <div className="summary-content">
              <span className="summary-value text-red-400">-{summary.totalStats?.totalPointsOut?.toLocaleString() || 0}</span>
              <span className="summary-label">{t('pointsOut', 'Points Out')}</span>
            </div>
          </div>

          {/* System Commission Card */}
          {systemStats && (
            <div className="summary-card summary-card-system">
              <div className="summary-icon" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                <DollarSign className="w-6 h-6" />
              </div>
              <div className="summary-content">
                <span className="summary-value text-emerald-400">{systemStats.allTime?.totalCommission?.toLocaleString() || 0}P</span>
                <span className="summary-label">{t('systemEarnings', 'System Earnings')}</span>
                <span className="summary-sub" style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                  {t('thisMonth', 'This Month')}: {systemStats.thisMonth?.totalCommission?.toLocaleString() || 0}P
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters Section */}
      <div className="transactions-filters">
        <div className="filters-header">
          <h4 className="filters-title">
            <Filter className="w-4 h-4" />
            {t('filters', 'Filters')}
          </h4>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="filters-toggle"
            >
              {showFilters ? t('hideFilters', 'Hide') : t('showFilters', 'Show')}
            </button>
            {user.role === 'main_admin' && (
              <button
                onClick={() => openDeleteModal('before')}
                className="filters-toggle"
                style={{ background: 'rgba(239, 68, 68, 0.2)', borderColor: '#ef4444', color: '#ef4444' }}
              >
                <Trash2 className="w-3 h-3" style={{ marginRight: '4px' }} />
                {t('bulkDelete', 'Bulk Delete')}
              </button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="filters-content">
            <div className="filter-group">
              <label className="filter-label">
                <FileText className="w-3 h-3" />
                {t('transactionType', 'Type')}
              </label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="filter-select"
              >
                {transactionTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">
                <Search className="w-3 h-3" />
                {t('search', 'Search')}
              </label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder={t('searchPlaceholder', 'Username or description...')}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label className="filter-label">
                <Calendar className="w-3 h-3" />
                {t('startDate', 'Start Date')}
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label className="filter-label">
                <Calendar className="w-3 h-3" />
                {t('endDate', 'End Date')}
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="filter-input"
              />
            </div>

            <div className="filter-actions">
              <button onClick={applyFilters} className="filter-btn filter-btn-apply">
                <Search className="w-4 h-4" />
                {t('apply', 'Apply')}
              </button>
              <button onClick={resetFilters} className="filter-btn filter-btn-reset">
                <RefreshCw className="w-4 h-4" />
                {t('reset', 'Reset')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Transactions List */}
      <div className="transactions-list">
        <div className="transactions-header">
          <h4 className="transactions-title">
            <History className="w-4 h-4" />
            {t('transactionLog', 'Transaction Log')}
            <span className="transactions-count">({pagination.total})</span>
          </h4>
          <button
            onClick={() => loadTransactions(pagination.page, filters)}
            disabled={localLoading}
            className="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${localLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {localLoading ? (
          <div className="transactions-loading">
            <RefreshCw className="w-6 h-6 animate-spin" />
            <span>{t('loading', 'Loading...')}</span>
          </div>
        ) : transactions.length === 0 ? (
          <div className="transactions-empty">
            <History className="w-12 h-12 text-gray-500" style={{ marginBottom: '12px' }} />
            <span style={{ fontSize: '1rem', fontWeight: '600', color: '#9ca3af' }}>
              {t('noTransactions', 'No transactions found')}
            </span>
            <span style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '8px', textAlign: 'center' }}>
              {t('transactionsWillAppear', 'Point transactions from tournaments, purchases, and admin actions will appear here.')}
            </span>
          </div>
        ) : (
          <div className="transactions-table-wrapper">
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>{t('time', 'Time')}</th>
                  <th>{t('type', 'Type')}</th>
                  <th>{t('user', 'User')}</th>
                  <th>{t('change', 'Change')}</th>
                  <th>{t('balance', 'Balance')}</th>
                  <th>{t('description', 'Description')}</th>
                  {user.role === 'main_admin' && <th style={{ width: '50px' }}></th>}
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx._id} className="transaction-row">
                    <td className="transaction-time">
                      <Clock className="w-3 h-3" />
                      <span>{tx.formattedTime}</span>
                    </td>
                    <td className="transaction-type">
                      {getTransactionIcon(tx.type, tx.metadata)}
                      <span className="type-label">{tx.typeLabel}</span>
                    </td>
                    <td className="transaction-user">
                      {tx.user ? (
                        <span className="user-info">
                          <span className="user-name">{tx.user.inGameName || tx.user.username}</span>
                          {tx.user.role !== 'player' && (
                            <span className={`user-role role-${tx.user.role}`}>
                              {tx.user.role}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-gray-500" style={{ fontStyle: 'italic' }}>
                          {tx.type === 'tournament_commission_system' ? 'System' : '-'}
                        </span>
                      )}
                    </td>
                    <td className={`transaction-change ${getPointsColorClass(tx.pointsChange)}`}>
                      {formatPoints(tx.pointsChange)}P
                    </td>
                    <td className="transaction-balance">
                      {tx.type === 'tournament_commission_system' ? '-' : `${tx.balanceAfter?.toLocaleString() || 0}P`}
                    </td>
                    <td className="transaction-description">
                      <span className="description-text">{tx.description}</span>
                      {tx.metadata?.reason && (
                        <span className="description-reason">({tx.metadata.reason})</span>
                      )}
                      {tx.processedBy && (
                        <span className="processed-by">
                          by {tx.processedBy.inGameName || tx.processedBy.username}
                        </span>
                      )}
                    </td>
                    {user.role === 'main_admin' && (
                      <td>
                        <button
                          onClick={() => openDeleteModal('single', tx)}
                          className="delete-tx-btn"
                          title={t('deleteTransaction', 'Delete transaction')}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="transactions-pagination">
            <button
              onClick={() => loadTransactions(pagination.page - 1, filters)}
              disabled={pagination.page === 1 || localLoading}
              className="pagination-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <span className="pagination-info">
              {t('page', 'Page')} {pagination.page} {t('of', 'of')} {pagination.totalPages}
            </span>

            <button
              onClick={() => loadTransactions(pagination.page + 1, filters)}
              disabled={pagination.page === pagination.totalPages || localLoading}
              className="pagination-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <h3>{t('deleteTransactions', 'Delete Transactions')}</h3>
              <button onClick={() => setShowDeleteModal(false)} className="modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="modal-content">
              {deleteMode === 'single' && deleteTarget && (
                <>
                  <p>{t('confirmDeleteSingle', 'Are you sure you want to delete this transaction?')}</p>
                  <div className="delete-preview">
                    <div><strong>{t('type', 'Type')}:</strong> {deleteTarget.typeLabel}</div>
                    <div><strong>{t('user', 'User')}:</strong> {deleteTarget.user?.inGameName || deleteTarget.user?.username || 'System'}</div>
                    <div><strong>{t('change', 'Change')}:</strong> {formatPoints(deleteTarget.pointsChange)}P</div>
                    <div><strong>{t('description', 'Description')}:</strong> {deleteTarget.description}</div>
                  </div>
                  <div className="modal-actions">
                    <button onClick={() => setShowDeleteModal(false)} className="modal-btn modal-btn-cancel">
                      {t('cancel', 'Cancel')}
                    </button>
                    <button onClick={handleDeleteSingle} className="modal-btn modal-btn-delete">
                      <Trash2 className="w-4 h-4" />
                      {t('delete', 'Delete')}
                    </button>
                  </div>
                </>
              )}

              {deleteMode === 'before' && (
                <>
                  <p>{t('deleteBefore', 'Delete all transactions before a specific date, or delete all transactions.')}</p>

                  <div className="delete-options">
                    <div className="delete-option">
                      <label>{t('deleteBeforeDate', 'Delete transactions before:')}</label>
                      <input
                        type="date"
                        value={deleteBeforeDate}
                        onChange={(e) => setDeleteBeforeDate(e.target.value)}
                        className="filter-input"
                      />
                      <button
                        onClick={handleDeleteBefore}
                        className="modal-btn modal-btn-delete"
                        disabled={!deleteBeforeDate}
                      >
                        <Trash2 className="w-4 h-4" />
                        {t('deleteBeforeBtn', 'Delete Before Date')}
                      </button>
                    </div>

                    <div className="delete-divider">
                      <span>{t('or', 'OR')}</span>
                    </div>

                    <div className="delete-option delete-option-danger">
                      <label>{t('deleteAllWarning', 'Delete ALL transactions (type DELETE ALL to confirm):')}</label>
                      <input
                        type="text"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder="DELETE ALL"
                        className="filter-input"
                        style={{ borderColor: '#ef4444' }}
                      />
                      <button
                        onClick={handleDeleteAll}
                        className="modal-btn modal-btn-delete-all"
                        disabled={deleteConfirmText !== 'DELETE ALL'}
                      >
                        <AlertTriangle className="w-4 h-4" />
                        {t('deleteAllBtn', 'Delete All Transactions')}
                      </button>
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button onClick={() => setShowDeleteModal(false)} className="modal-btn modal-btn-cancel">
                      {t('cancel', 'Cancel')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionsTab;
